import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { useAuth } from './AuthContext';
import './LoginView.css';
import { resolveAuthUserSession } from '../services/authSessionApi';

const LoginView = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotStatus, setForgotStatus] = useState(null);
  const navigate = useNavigate();
  const { setUserRole, setSessionUser } = useAuth();

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotStatus('sending');
    const auth = getAuth();
    try {
      await sendPasswordResetEmail(auth, forgotEmail.trim().toLowerCase(), {
        url: 'https://bbtspr.com/login',
        handleCodeInApp: false,
      });
      setForgotStatus('sent');
    } catch (err) {
      console.error('[forgotPassword]', err.code, err.message);
      if (err.code === 'auth/user-not-found') {
        setForgotStatus('not-found');
      } else {
        setForgotStatus('error');
      }
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    const auth = getAuth();

    console.log('[handleLogin] 🔐 Iniciando sesión...');
    console.log(`[handleLogin] email: ${email}`);

    try {
      await signInWithEmailAndPassword(auth, email, password);
      const session = await resolveAuthUserSession();
      const userData = session?.user || null;
      console.log('[handleLogin] 📄 Datos Firestore:', userData);

      const effectiveRole = userData?.accessRole || userData?.role || null;

      if (!effectiveRole) {
        setError('Tu cuenta no tiene rol asignado. Contacta al administrador.');
        return;
      }

      setUserRole(effectiveRole);
      setSessionUser(userData);
      console.log(`[handleLogin] 🚀 Rol: ${effectiveRole}`);

      if (effectiveRole === 'admin') {
        navigate('/admin/dashboard');
      } else if (effectiveRole === 'client') {
        navigate('/clients/dashboard');
      } else if (['seller', 'services', 'installations', 'demos'].includes(effectiveRole)) {
        navigate('/staff/profile');
      } else {
        setError('Rol de usuario no reconocido.');
      }

    } catch (err) {
      console.error('[handleLogin] ❌ Error Firebase:', err.code, err.message);
      if (err.code === 'auth/user-not-found') {
        setError('Usuario no encontrado.');
      } else if (err.code === 'auth/wrong-password') {
        setError('Contraseña incorrecta.');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Demasiados intentos. Intenta más tarde.');
      } else {
        setError(err?.message || 'Credenciales inválidas o error inesperado.');
      }
    }
  };

  return (
    <div className="min-h-screen bg-cover bg-center relative flex items-center justify-center px-4"
      style={{
        backgroundImage: "url('https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&w=1650&q=80')" // Puedes cambiarla por una tuya
      }}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-black bg-opacity-60"></div>


      {/* Login Box */}
      <div className="relative z-10 bg-white rounded-2xl shadow-lg p-8 max-w-md w-full">
        <div className="flex flex-col items-center mb-6">
          <img src="/logo-bbts.png" alt="Logo BBTS" className="h-16 mb-4" />
          <h2 className="text-2xl font-bold text-gray-800">
            {showForgot ? 'Recuperar contraseña' : 'Ingreso a BBTS'}
          </h2>
        </div>

        {showForgot ? (
          <div className="space-y-4">
            {forgotStatus === 'sent' ? (
              <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-4 text-center space-y-2">
                <p className="text-green-700 font-semibold text-sm">Correo enviado</p>
                <p className="text-green-600 text-sm">
                  Revisa tu bandeja de entrada en <strong>{forgotEmail}</strong> y haz clic en el enlace para crear tu contraseña.
                </p>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <p className="text-sm text-gray-500 text-center">
                  Ingresa tu correo y te enviaremos un enlace para restablecer tu contraseña.
                </p>
                <input
                  type="email"
                  placeholder="Correo electrónico"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
                {forgotStatus === 'not-found' && (
                  <p className="text-sm text-red-500 text-center">No encontramos una cuenta con ese correo.</p>
                )}
                {forgotStatus === 'error' && (
                  <p className="text-sm text-red-500 text-center">Error al enviar el correo. Intenta de nuevo.</p>
                )}
                <button
                  type="submit"
                  disabled={forgotStatus === 'sending'}
                  className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-semibold py-2 rounded-md transition"
                >
                  {forgotStatus === 'sending' ? 'Enviando…' : 'Enviar enlace'}
                </button>
              </form>
            )}
            <button
              type="button"
              onClick={() => { setShowForgot(false); setForgotStatus(null); setForgotEmail(''); }}
              className="w-full text-sm text-gray-500 hover:text-gray-700 text-center"
            >
              ← Volver al ingreso
            </button>
          </div>
        ) : (
          <form onSubmit={handleLogin} className="space-y-4">
            <input
              type="email"
              placeholder="Correo electrónico"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
            <input
              type="password"
              placeholder="Contraseña"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
            <button
              type="submit"
              className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-2 rounded-md transition"
            >
              Ingresar
            </button>
            {error && <p className="text-sm text-red-500 text-center">{error}</p>}
            <p className="text-center">
              <button
                type="button"
                onClick={() => { setShowForgot(true); setForgotStatus(null); }}
                className="text-sm text-orange-500 hover:text-orange-700 hover:underline"
              >
                ¿Olvidaste tu contraseña?
              </button>
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default LoginView;
